import React, { useRef, useCallback, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  ColumnResizeMode,
} from '@tanstack/react-table'
import { FixedSizeList as List, ListChildComponentProps } from 'react-window'
import { cn } from '@/lib/utils'

export interface SearchMatch {
  rowIndex: number
  colIndex: number
  occurrence: number
}

export interface VirtualizedDataTableHandle {
  scrollToRow: (rowIndex: number) => void
}

interface VirtualizedDataTableProps {
  columns: string[]
  rows: unknown[][]
  height?: number
  rowHeight?: number
  className?: string
  searchQuery?: string
  activeMatch?: SearchMatch | null
  /**
   * Name of a column to emphasise and scroll into view. Distinct from
   * `searchQuery`, which scans CELL VALUES — a column name is never a cell, so
   * searching for one finds nothing. Used to deep-link to a signal (Tag mapping
   * → a matched timeseries tag).
   */
  highlightColumn?: string | null
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightCellText(
  text: string,
  query: string,
  isActiveCell: boolean,
  activeOccurrence: number,
): React.ReactNode {
  if (!query || !text) return text
  const escaped = escapeRegex(query)
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = text.split(regex)
  if (parts.length === 1) return text

  let occurrence = 0
  return parts.map((part, i) => {
    if (i % 2 === 0) return part || null
    const isActive = isActiveCell && occurrence === activeOccurrence
    occurrence++
    return (
      <mark
        key={i}
        style={{
          backgroundColor: isActive ? 'rgb(59, 130, 246)' : 'rgba(255, 235, 0, 0.6)',
          color: isActive ? 'white' : 'inherit',
          borderRadius: '2px',
          padding: '0 1px',
        }}
      >
        {part}
      </mark>
    )
  })
}

const VirtualizedDataTable = forwardRef<VirtualizedDataTableHandle, VirtualizedDataTableProps>(({
  columns,
  rows,
  height = 400,
  rowHeight = 35,
  className,
  searchQuery,
  activeMatch,
  highlightColumn,
}, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<List>(null)
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange')
  const highlightedHeaderRef = useRef<HTMLDivElement>(null)

  /**
   * Column ids are positional (`col_<index>`), not names, so a deep link that
   * names a signal has to be resolved to its index first. Case-insensitive:
   * historian exports vary the casing of the same tag between files.
   */
  const highlightedColumnId = useMemo(() => {
    if (!highlightColumn) return null
    const wanted = highlightColumn.trim().toLowerCase()
    const index = columns.findIndex((col) => col.trim().toLowerCase() === wanted)
    return index >= 0 ? `col_${index}` : null
  }, [highlightColumn, columns])

  // Bring a deep-linked column into view. A wide historian export is hundreds
  // of columns; highlighting one off-screen would look like nothing happened.
  useEffect(() => {
    if (!highlightedColumnId) return
    const timer = setTimeout(
      () => highlightedHeaderRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' }),
      120,
    )
    return () => clearTimeout(timer)
  }, [highlightedColumnId])

  useImperativeHandle(ref, () => ({
    scrollToRow: (rowIndex: number) => {
      listRef.current?.scrollToItem(rowIndex, 'center')
    },
  }))

  // Scroll to active match row when it changes
  useEffect(() => {
    if (activeMatch) {
      listRef.current?.scrollToItem(activeMatch.rowIndex, 'center')
    }
  }, [activeMatch])

  // Calculate column width based on header and sample data
  const getColumnWidth = useCallback((colName: string, colIndex: number) => {
    const headerWidth = Math.max(colName.length * 10, 80)
    let maxContentWidth = 0
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const cellValue = rows[i]?.[colIndex]
      if (cellValue !== null && cellValue !== undefined) {
        const contentLength = String(cellValue).length
        maxContentWidth = Math.max(maxContentWidth, contentLength * 8)
      }
    }
    return Math.min(Math.max(headerWidth, maxContentWidth, 120), 500)
  }, [rows])

  // Convert column names to react-table column definitions
  const columnDefs: ColumnDef<unknown[]>[] = useMemo(() => {
    return columns.map((col, index) => ({
      id: `col_${index}`,
      accessorFn: (row: unknown[]) => row[index],
      header: col.includes('Unnamed') || !col ? '' : col,
      cell: (info) => {
        const value = info.getValue()
        if (value === null || value === undefined) return ''
        return String(value)
      },
      size: getColumnWidth(col, index),
      minSize: 50,
      maxSize: 800,
    }))
  }, [columns, getColumnWidth])

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    columnResizeMode,
    getCoreRowModel: getCoreRowModel(),
  })

  const { rows: tableRows } = table.getRowModel()
  const headerGroups = table.getHeaderGroups()

  // Calculate total width based on actual column sizes
  const totalWidth = useMemo(() => {
    return table.getAllColumns().reduce((sum, col) => sum + col.getSize(), 0)
  }, [table, table.getState().columnSizing])

  // Row renderer for react-window
  const Row = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const row = tableRows[index]
      if (!row) return null

      return (
        <div
          style={{
            ...style,
            display: 'flex',
            width: totalWidth,
          }}
          className={cn(
            'border-b transition-colors hover:bg-muted/50',
            index % 2 === 0 ? 'bg-white dark:bg-dark-card' : 'bg-gray-50/50 dark:bg-dark-muted/50'
          )}
        >
          {row.getVisibleCells().map((cell, colIdx) => {
            const cellValue = String(cell.getValue() ?? '')
            const isActiveCell = activeMatch?.rowIndex === index && activeMatch?.colIndex === colIdx
            const cellContent = searchQuery
              ? highlightCellText(cellValue, searchQuery, !!isActiveCell, activeMatch?.occurrence ?? 0)
              : flexRender(cell.column.columnDef.cell, cell.getContext())

            return (
              <div
                key={cell.id}
                className="p-2 align-middle overflow-hidden text-ellipsis whitespace-nowrap border-r last:border-r-0"
                style={{
                  width: cell.column.getSize(),
                  minWidth: cell.column.getSize(),
                  maxWidth: cell.column.getSize(),
                }}
                title={cellValue}
              >
                {cellContent}
              </div>
            )
          })}
        </div>
      )
    },
    [tableRows, totalWidth, searchQuery, activeMatch]
  )

  if (rows.length === 0) {
    return (
      <div className="p-4 text-gray-500 dark:text-dark-text-muted text-center">No data available</div>
    )
  }

  return (
    <div
      className={cn('flex flex-col flex-1 min-h-0', className)}
      ref={tableContainerRef}
      data-selectable-table
    >
      {/* Single scroll container for both header and rows */}
      <div className="overflow-x-auto flex-1 min-h-0">
        <div style={{ width: totalWidth, minWidth: '100%' }}>
          {/* Header with resize handles */}
          <div className="flex border-b bg-gray-100 dark:bg-dark-accent sticky top-0 z-10">
            {headerGroups.map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <div
                  key={header.id}
                  ref={
                    highlightedColumnId && header.column.id === highlightedColumnId
                      ? highlightedHeaderRef
                      : undefined
                  }
                  className={cn(
                    'h-10 px-2 text-left align-middle font-medium border-r last:border-r-0 flex items-center relative group',
                    highlightedColumnId && header.column.id === highlightedColumnId
                      ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                      : 'text-muted-foreground bg-gray-100 dark:bg-dark-accent',
                  )}
                  style={{
                    width: header.column.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {/* Resize handle */}
                  <div
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className={cn(
                      'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-blue-500 group-hover:bg-gray-300',
                      header.column.getIsResizing() && 'bg-blue-500'
                    )}
                  />
                </div>
              ))
            )}
          </div>

          {/* Virtualized rows */}
          <List
            ref={listRef}
            height={Math.max(height - 40, 100)}
            itemCount={tableRows.length}
            itemSize={rowHeight}
            width={totalWidth}
            className="scrollbar-thin"
          >
            {Row}
          </List>
        </div>
      </div>
    </div>
  )
})

VirtualizedDataTable.displayName = 'VirtualizedDataTable'

export default VirtualizedDataTable
